import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
  Bundle,
  ServerResponse,
  BundlePurchasePayload,
} from "../../../../types";
import { unlockBundle } from "../../hooks/useSlideInMenu";
import { usePaymentMutation } from "../../../payments/hooks/usePaymentMutation";
import { StripePaymentConfigTypes } from "../../../payments/components/StripePayment";
import { useRefetchAccountAndRelease } from "../../../../hooks/useRefetchAccountAndRelease";

export interface BundlePurchaseConfig {
  bundle: Bundle;
  isGold: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function useBundlePurchase(config: BundlePurchaseConfig) {
  const { bundle, isGold, onSuccess, onError } = config;
  const queryClient = useQueryClient();

  const refetchQueries = useRefetchAccountAndRelease({
    invalidateUser: true, // Invalidate user data to update ownership state
    invalidateReader: false,
    invalidateReleaseInfo: false,
    extraKeys: [
      ["bundleDetail", bundle.uuid], // Refetch specific bundle details
      // Invalidate specific bundle entity caches
      [`Bundle-${bundle.id}`],
      [`bundle-${bundle.uuid}`],
    ],
  });

  const createPayload = (
    stripePaymentConfig: StripePaymentConfigTypes,
  ): BundlePurchasePayload => {
    const pricePoint = isGold
      ? bundle.ppb_gold_sale_price_point || bundle.ppb_gold_price_point
      : bundle.ppb_sale_price_point || bundle.ppb_price_point;

    const price = isGold ? bundle.gold_price : bundle.price;
    const isSale = !!bundle.ppb_sale_price_point;

    // Calculate full price from all items
    const fullPrice = bundle.items.reduce((total, item) => {
      return total + item.ppb_price_point / 100; // Convert cents to dollars
    }, 0);

    const payload: BundlePurchasePayload = {
      price_point_id: pricePoint,
      currency_code: "USD",
      raw_price: price.price_float_discounted,
      full_price: fullPrice,
      is_gold: isGold,
      is_sale: isSale,
      stripeToken: stripePaymentConfig.stripeToken,
      ownerPaymentServiceTokenId: stripePaymentConfig.ownerPaymentServiceTokenId
        ? parseInt(stripePaymentConfig.ownerPaymentServiceTokenId, 10)
        : undefined,
    };

    // Add dynamic raw_price_* fields for each item's price point
    bundle.items.forEach((item) => {
      const key = `raw_price_${item.ppb_price_point}`;
      (payload as any)[key] = item.ppb_price_point / 100;
    });

    return payload;
  };

  const mutationFn = (stripePaymentConfig: StripePaymentConfigTypes) => {
    const payload = createPayload(stripePaymentConfig);
    return unlockBundle(bundle.uuid, payload);
  };

  const handleSuccess = async () => {
    // Use the optimized refetchQueries approach with selective invalidation
    await refetchQueries();

    // Only invalidate specific bundle queries that might not be covered by refetchQueries
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        return (
          // Target specific bundle queries
          (Array.isArray(queryKey) && queryKey[0] === "bundles") ||
          (Array.isArray(queryKey) &&
            queryKey.includes(`Bundle-${bundle.id}`)) ||
          (Array.isArray(queryKey) &&
            queryKey.includes(`bundle-${bundle.uuid}`))
        );
      },
    });

    onSuccess?.();
  };

  const handleError = (error: Error): string => {
    let errorMessage = "An error occurred during purchase";

    if (axios.isAxiosError<ServerResponse<string>>(error)) {
      const serverError = error.response?.data?.payload?.results;
      if (typeof serverError === "string") {
        errorMessage = serverError;
      } else if (error.response?.data?.payload?.form_fields) {
        // Handle validation errors
        const formFields = error.response.data.payload.form_fields;
        if (Array.isArray(formFields) && formFields.length > 0) {
          errorMessage = formFields[0].error || errorMessage;
        }
      }
    }

    onError?.(errorMessage);
    return errorMessage;
  };

  return usePaymentMutation(mutationFn, handleSuccess, handleError);
}
